import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

const BackendPagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  loading = false,
  className = ""
}) => {
  const numeroDePages = genererPagination(currentPage, totalPages);

  // Calculer les indices des éléments affichés
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  // Handler pour passer d'une page à l'autre
  const handleChangePage = (direction) => {
    if (loading) return;
    
    if (direction === 'précédente') {
      onPageChange(Math.max(currentPage - 1, 1));
    } else if (direction === 'suivante') {
      onPageChange(Math.min(currentPage + 1, totalPages));
    }
  };

  const handlePageCourante = (page) => {
    if (loading) return;
    
    const numeroDePage = Number(page);
    if (!isNaN(numeroDePage) && numeroDePage >= 1 && numeroDePage <= totalPages) {
      onPageChange(numeroDePage);
    }
  };

  if (totalPages <= 1) {
    return (
      <div className={`flex items-center justify-between w-full mt-4 ${className}`}>
        <div className="text-sm text-gray-500">
          {totalItems > 0 ? (
            <>
              Affichage de {startIndex} à {endIndex} sur {totalItems} élément(s)
            </>
          ) : (
            'Aucun élément trouvé'
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative flex items-center justify-between w-full mt-5 ${className}`}>
      {/* Informations sur les éléments affichés */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-500">
          {totalItems > 0 ? (
            <>
              Affichage de {startIndex} à {endIndex} sur {totalItems} élément(s)
            </>
          ) : (
            'Aucun élément trouvé'
          )}
        </div>
        
        {/* Sélecteur d'éléments par page */}
        {onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Afficher:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(parseInt(e.target.value))}
              disabled={loading}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-gray-500">par page</span>
          </div>
        )}
      </div>

      {/* Contrôles de pagination */}
      <div className="flex items-center gap-1">
        {currentPage > 1 && (
          <BoutonPagination
            onClick={() => handleChangePage('précédente')}
            title={`Voir la page précédente`}
            btnIcon={faArrowLeft}
            disabled={loading}
          />
        )}
        
        {numeroDePages.map((page, index) => (
          <BoutonNumeroDesPage
            handleChangePage={() => handlePageCourante(page)}
            currentPage={currentPage}
            page={page}
            key={index}
            disabled={loading}
          />
        ))}
        
        <em className="mx-2 text-sm text-gray-500">
          Page {currentPage} sur {totalPages}
        </em>
        
        {currentPage < totalPages && (
          <BoutonPagination
            onClick={() => handleChangePage('suivante')}
            title={`Voir la page suivante`}
            btnIcon={faArrowRight}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
};

export default BackendPagination;

// Fonction pour générer les numéros de page
const genererPagination = (pageCourante, totalDesPages) => {
  if (totalDesPages <= 7) {
    return Array.from({ length: totalDesPages }, (_, indice) => indice + 1);
  }
  
  if (pageCourante <= 3) {
    return [1, 2, 3, '...', totalDesPages - 1, totalDesPages];
  }
  
  if (pageCourante >= totalDesPages - 2) {
    return [1, 2, '...', totalDesPages - 2, totalDesPages - 1, totalDesPages];
  }

  return [
    1,
    '...',
    pageCourante - 1,
    pageCourante,
    pageCourante + 1,
    '...',
    totalDesPages,
  ];
};

// Composant bouton de numéro de page
const BoutonNumeroDesPage = ({ currentPage, page, handleChangePage, disabled }) => {
  const pageActive = currentPage === page;
  return (
    <BoutonPagination
      onClick={handleChangePage}
      disabled={pageActive || disabled}
      title={
        pageActive || page === '...'
          ? ''
          : 'Voir les données de cette page N° ' + page
      }
      isActive={pageActive}
      btnText={page}
    />
  );
};

// Composant bouton de pagination
const BoutonPagination = ({ onClick, btnText, btnIcon, isActive, title, disabled }) => {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`flex items-center border rounded justify-center h-[30px] w-[30px] text-sm transition-colors ${
        isActive
          ? 'border-[#2a5f85] text-white-A700 outline outline-[#09aedb] cursor-not-allowed bg-[#2a5f85]'
          : disabled
          ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
          : 'hover:text-white-A700 bg-gray-100 hover:bg-[#307083] border-[#307083] cursor-pointer'
      }`}
    >
      {btnText ? btnText : ''}
      {btnIcon ? <FontAwesomeIcon icon={btnIcon} /> : ''}
    </button>
  );
};
